import { lazy, ReactNode, Suspense, SuspenseProps } from 'react';

import ColumnsBlockContent from 'src/common/components/page-blocks/ColumnsBlockContent';
import ButtonLinkBlockContent from 'src/common/components/page-blocks/ButtonLinkBlockContent';
import CalendarBlockContent from 'src/common/components/page-blocks/CalendarBlockContent';
import CompetitionsListBlockContent from 'src/common/components/page-blocks/CompetitionsListBlockContent';
import CountdownBlockContent from 'src/common/components/page-blocks/CountdownBlockContent';
import ImageBlockContent from 'src/common/components/page-blocks/ImageBlockContent';
import LogoBlockContent from 'src/common/components/page-blocks/LogoBlockContent';
import DividerBlockContent from 'src/common/components/page-blocks/DividerBlockContent';
import TextBlockContent from 'src/common/components/page-blocks/TextBlockContent';
import { LanguageKeys } from 'src/common/constants/languages';
import { resolvePageBlockDate } from 'src/helpers/pageBlockDateUtils';
import { PlaceholderRenderContext } from 'src/helpers/placeholderRegistry';
import { IEdition } from 'src/interfaces/Edition';
import { VisibilityContext } from 'src/helpers/visibilityFilter';
import {
  ICalendarBlock,
  ICountdownBlock,
  IColumnsBlock,
  IDividerBlock,
  IPageBlock,
} from 'src/interfaces/PageBlock';

const MapBlockContent = lazy(() => import('src/common/components/page-blocks/MapBlockContent'));
const TrackMapBlockContent = lazy(
  () => import('src/common/components/page-blocks/TrackMapBlockContent'),
);
const SponsorsBlockContent = lazy(
  () => import('src/common/components/page-blocks/SponsorsBlockContent'),
);
const ResultsBlockContent = lazy(
  () => import('src/common/components/page-blocks/ResultsBlockContent'),
);
const SignupFlowBlockContent = lazy(
  () => import('src/common/components/page-blocks/SignupFlowBlockContent'),
);
const SignupBreakdownBlockContent = lazy(
  () => import('src/common/components/page-blocks/SignupBreakdownBlockContent'),
);

function LazyBlockFallback() {
  return null;
}

function LazyBlock({ children }: { children: SuspenseProps['children'] }) {
  return <Suspense fallback={<LazyBlockFallback />}>{children}</Suspense>;
}

interface RenderPageBlockOptions {
  lang: LanguageKeys;
  placeholderContext: PlaceholderRenderContext;
  currentEdition?: IEdition;
  inHero?: boolean;
  visibilityContext?: VisibilityContext;
  applyVisibilityRules?: boolean;
  contentKey?: string;
}

export function renderPageBlock(
  block: IPageBlock,
  {
    lang,
    placeholderContext,
    currentEdition,
    inHero,
    visibilityContext,
    applyVisibilityRules = true,
    contentKey = 'signup',
  }: RenderPageBlockOptions,
): ReactNode {
  switch (block.type) {
    case 'text':
      return (
        <TextBlockContent
          key={block.id}
          block={block}
          lang={lang}
          placeholderContext={placeholderContext}
          inHero={inHero}
        />
      );
    case 'divider':
      return <DividerBlockContent key={block.id} block={block as IDividerBlock} />;
    case 'trackMap':
      return (
        <LazyBlock key={block.id}>
          <TrackMapBlockContent block={block} />
        </LazyBlock>
      );
    case 'map':
      return (
        <LazyBlock key={block.id}>
          <MapBlockContent block={block} />
        </LazyBlock>
      );
    case 'sponsors':
      return (
        <LazyBlock key={block.id}>
          <SponsorsBlockContent lang={lang} />
        </LazyBlock>
      );
    case 'competitionsList':
      return <CompetitionsListBlockContent key={block.id} block={block} contentKey={contentKey} />;
    case 'results':
      return (
        <LazyBlock key={block.id}>
          <ResultsBlockContent block={block} />
        </LazyBlock>
      );
    case 'logo':
      return (
        <LogoBlockContent
          key={block.id}
          block={block}
          lang={lang}
          contentKey={contentKey}
          inHero={inHero}
        />
      );
    case 'image':
      return <ImageBlockContent key={block.id} block={block} lang={lang} />;
    case 'calendar': {
      const eventDate = resolvePageBlockDate(block as ICalendarBlock, currentEdition);
      if (!eventDate) return null;
      return <CalendarBlockContent key={block.id} eventDate={eventDate} />;
    }
    case 'countdown': {
      const eventDate = resolvePageBlockDate(block as ICountdownBlock, currentEdition);
      if (!eventDate) return null;
      return <CountdownBlockContent key={block.id} block={block} eventDate={eventDate} />;
    }
    case 'button':
      return <ButtonLinkBlockContent key={block.id} block={block} lang={lang} />;
    case 'columns': {
      if (!visibilityContext) return null;
      return (
        <ColumnsBlockContent
          key={block.id}
          block={block as IColumnsBlock}
          lang={lang}
          visibilityContext={visibilityContext}
          placeholderContext={placeholderContext}
          currentEdition={currentEdition}
          inHero={inHero}
          applyVisibilityRules={applyVisibilityRules}
          contentKey={contentKey}
        />
      );
    }
    case 'signupFlow':
      return (
        <LazyBlock key={block.id}>
          <SignupFlowBlockContent contentKey={contentKey} />
        </LazyBlock>
      );
    case 'signupBreakdown':
      return (
        <LazyBlock key={block.id}>
          <SignupBreakdownBlockContent />
        </LazyBlock>
      );
    default:
      return null;
  }
}
